'use client'

import type { LocationFacetType } from '@components/app/SearchLandingPage/Hooks/useLocationFacetRefinement'
import { classMerge } from '@components/utilities/classMerge'
import Routes from '@lib/routes'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import type { TrackingSources } from '@lib/tracking/TrackingSources'
import type { FacetType } from '@modules/trials/search/const'
import createSlugFromFacets from '@modules/trials/search/facetFilters/createSlugFromFacets'
import { type ReactNode, Suspense, useEffect, useRef, useState } from 'react'
import { ConditionAutocomplete } from './ConditionAutocomplete'
import LocationAutoComplete from './LocationAutoComplete'
import { SearchTrialsButton } from './SearchTrialsButton.client'

/**
 * Homepage SearchWidget to assemble a slug compatible with the search lander page
 */
export default function SearchWidget({
  className,
  source,
}: {
  className?: string
  source?: TrackingSources
}) {
  // Location Autocomplete State
  const [inputLocation, setInputLocation] = useState('')
  const [isLocationSelected, setIsLocationSelected] = useState(false)
  const [locationFacetType, setLocationFacetType] = useState<
    LocationFacetType | undefined
  >(undefined)

  // Condition Autocomplete State
  const [inputCondition, setInputCondition] = useState('')
  const [isConditionSelected, setIsConditionSelected] = useState(false)

  // Search URL State
  const [searchSlug, setSearchSlug] = useState<string | undefined>(undefined)

  // Support for focusing the location input after the condition input is selected
  const locationInputRef = useRef<HTMLInputElement>(null)
  const conditionInputRef = useRef<HTMLInputElement>(null)

  const handleFocusLocationInput = () => {
    locationInputRef.current?.focus()
  }

  useEffect(() => {
    if (isConditionSelected) {
      handleFocusLocationInput()
    }
  }, [isConditionSelected])

  useEffect(() => {
    // Build the link to the search page
    const facets: Partial<Record<FacetType, string>> = {}

    // Determine which type of location face to use
    if (locationFacetType === 'city') {
      facets.city = inputLocation
    } else if (locationFacetType === 'state') {
      facets.state = inputLocation
    }

    // Create a search lander slug from the facet filters
    const slug = createSlugFromFacets({
      ...facets,
      condition: inputCondition,
    })
    setSearchSlug(slug)
  }, [inputCondition, inputLocation])

  return (
    <div className={className}>
      <div>
        <div className='z-filterBar mx-auto max-w-5xl'>
          <div
            className={classMerge(
              'group relative mx-auto flex flex-row items-center rounded-full border-2 border-neutral100 bg-white focus-within:border-neutral100 focus-within:bg-neutral100',
              'transition-all duration-200 focus-within:shadow-2xl hover:shadow-2xl',
              'animate-pulse-shadow',
            )}
          >
            {/* Condition section */}
            <div
              className={classMerge(
                'h-20 w-5/12 cursor-pointer rounded-full py-2 pl-10 pr-6 transition-colors focus-within:bg-white hover:bg-neutral100 focus-within:hover:bg-white',
              )}
            >
              <LabeledInput
                className='flex cursor-pointer flex-col pt-2'
                htmlFor={'search-bar-condition-input'}
                label='Condition'
              >
                <ConditionAutocomplete
                  focusNextInput={handleFocusLocationInput}
                  inputCondition={inputCondition}
                  inputRef={conditionInputRef}
                  isConditionSelected={isConditionSelected}
                  setInputCondition={setInputCondition}
                  setIsConditionSelected={setIsConditionSelected}
                />
              </LabeledInput>
            </div>

            {/* Vertical divider */}
            <div className='h-16 w-0.5 bg-neutral100 transition-opacity duration-200 group-focus-within:opacity-0 group-hover:opacity-0' />

            {/* Location section */}
            <div
              className={classMerge(
                'relative flex h-20 w-7/12 justify-between rounded-full py-2 pl-6 transition-colors focus-within:bg-white hover:bg-neutral100 focus-within:hover:bg-white',
              )}
              onClick={handleFocusLocationInput}
            >
              <LabeledInput
                className='flex w-1/2 cursor-pointer flex-col pt-2'
                htmlFor={'search-bar-location-input'}
                label='Location'
              >
                <div className='flex flex-row items-start justify-start'>
                  <LocationAutoComplete
                    inputLocation={inputLocation}
                    inputRef={locationInputRef}
                    isLocationSelected={isLocationSelected}
                    setInputLocation={setInputLocation}
                    setIsLocationSelected={setIsLocationSelected}
                    setLocationFacetType={setLocationFacetType}
                  />
                </div>
              </LabeledInput>
              <SearchTrialsButton
                className='mr-2 hidden h-16 w-56 items-center rounded-full px-6 md:flex'
                href={
                  searchSlug
                    ? absoluteUrl(DynamicRoutes.search(searchSlug))
                    : absoluteUrl(Routes.search) // Fallback to just search page if no slug is set
                }
                source={source}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const HomePageSearchWidget = ({
  source,
}: {
  source: TrackingSources
}) => {
  return (
    <>
      <div className='hidden w-full lg:block'>
        <Suspense fallback={null}>
          <SearchWidget className='w-full' source={source} />
        </Suspense>
      </div>
      <SearchTrialsButton
        className='lg:hidden'
        href={Routes.search}
        source={source}
      />
    </>
  )
}

/**
 * Renders an input with an associated label
 * @param children - The input component to be labeled
 * @param htmlFor - HTML id to associate the label with its input
 * @param label - Text to display as the label
 */
const LabeledInput = ({
  children,
  className,
  htmlFor,
  label,
}: {
  children: ReactNode
  className?: string
  htmlFor: string
  label: string
}) => {
  return (
    <div className={className}>
      <label
        className='cursor-pointer text-sm font-normal leading-[140%]'
        htmlFor={htmlFor}
      >
        {label}
      </label>
      {children}
    </div>
  )
}
